import { useState, useEffect } from 'react';
import { paymentsAPI, bankingAPI } from '../../api';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';

/**
 * Bulk payment modal for recording a single lump-sum payment across multiple invoices.
 * Used from Invoices list when multiple invoices are selected.
 *
 * @param {Array} invoices - Array of invoice objects with id, invoiceNum, client, balanceDue
 * @param {boolean} open - Whether the modal is open
 * @param {function} onClose - Callback when user closes the modal
 * @param {function} onSuccess - Callback after successful bulk payment
 */
const BulkPaymentModal = ({ invoices = [], open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    bank_account_id: '',
    total_amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    reference: '',
    notes: '',
  });

  const [allocations, setAllocations] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      fetchBankAccounts();
      // Initialize allocations from invoices
      setAllocations(
        invoices.map((inv) => ({
          invoice_id: inv.id,
          invoice_num: inv.invoiceNum,
          client_name: getClientDisplayName(inv.client),
          balance_due: parseFloat(inv.balanceDue) || 0,
          amount: '',
        }))
      );
      setFormData((prev) => ({
        ...prev,
        payment_date: new Date().toISOString().split('T')[0],
      }));
      setError('');
    }
  }, [open, invoices]);

  const getClientDisplayName = (client) => {
    if (!client) return '—';
    if (client.clientType === 'company') {
      return client.companyName || 'Unnamed Company';
    }
    return `${client.firstName || ''} ${client.lastName || ''}`.trim() || 'Unnamed Individual';
  };

  const fetchBankAccounts = async () => {
    try {
      setLoadingAccounts(true);
      const accounts = await bankingAPI.getAccounts({ include_inactive: false });
      const accountsArray = Array.isArray(accounts) ? accounts : [];
      setBankAccounts(accountsArray);
      if (accountsArray.length > 0) {
        setFormData((prev) => ({ ...prev, bank_account_id: accountsArray[0].id }));
      }
    } catch (err) {
      console.error('Error fetching bank accounts:', err);
      setError('Failed to load bank accounts. Please try again.');
    } finally {
      setLoadingAccounts(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount || 0);
  };

  const sumAllocations = () => {
    return allocations.reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0);
  };

  const totalBalanceDue = allocations.reduce((sum, a) => sum + (a.balance_due || 0), 0);

  const handleAllocationChange = (index, value) => {
    const num = parseFloat(value);
    const valid = !isNaN(num) && num >= 0;
    setAllocations((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], amount: valid ? value : '' };
      return next;
    });
  };

  const handlePayFull = (index) => {
    setAllocations((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], amount: next[index].balance_due.toFixed(2) };
      return next;
    });
  };

  const handleDistributeEqually = () => {
    const total = parseFloat(formData.total_amount) || 0;
    if (total <= 0 || allocations.length === 0) return;
    const perRow = total / allocations.length;
    const rounded = Math.floor(perRow * 100) / 100;
    const remainder = Math.round((total - rounded * allocations.length) * 100) / 100;
    setAllocations((prev) =>
      prev.map((a, i) => ({
        ...a,
        amount: (rounded + (i === 0 ? remainder : 0)).toFixed(2),
      }))
    );
  };

  const handleDistributeByBalance = () => {
    const total = parseFloat(formData.total_amount) || 0;
    if (total <= 0 || totalBalanceDue <= 0 || allocations.length === 0) return;
    let allocated = 0;
    const newAllocs = allocations.map((a, i) => {
      const isLast = i === allocations.length - 1;
      const amt = isLast
        ? Math.round((total - allocated) * 100) / 100
        : Math.floor(total * (a.balance_due / totalBalanceDue) * 100) / 100;
      allocated += amt;
      return { ...a, amount: amt.toFixed(2) };
    });
    setAllocations(newAllocs);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const totalAmount = parseFloat(formData.total_amount);
    if (!formData.bank_account_id) {
      setError('Please select a bank account');
      return false;
    }
    if (!totalAmount || totalAmount <= 0) {
      setError('Please enter a valid total amount');
      return false;
    }
    const sum = sumAllocations();
    if (Math.abs(sum - totalAmount) > 0.01) {
      setError(
        `Sum of allocations (${formatCurrency(sum)}) must equal total amount (${formatCurrency(totalAmount)})`
      );
      return false;
    }
    for (const a of allocations) {
      const amt = parseFloat(a.amount) || 0;
      if (amt > a.balance_due) {
        setError(
          `Amount for invoice ${a.invoice_num} (${formatCurrency(amt)}) exceeds balance due (${formatCurrency(a.balance_due)})`
        );
        return false;
      }
      if (amt < 0) {
        setError('Allocation amounts cannot be negative');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validate()) return;

    setLoading(true);
    try {
      const payload = {
        bank_account_id: formData.bank_account_id,
        total_amount: parseFloat(formData.total_amount),
        payment_date: formData.payment_date || new Date().toISOString().split('T')[0],
        allocations: allocations
          .filter((a) => (parseFloat(a.amount) || 0) > 0)
          .map((a) => ({
            invoice_id: a.invoice_id,
            amount: parseFloat(a.amount),
          })),
        ...(formData.reference?.trim() ? { reference: formData.reference.trim() } : {}),
        ...(formData.notes?.trim() ? { notes: formData.notes.trim() } : {}),
      };

      await paymentsAPI.createBulk(payload);
      onSuccess();
      onClose();
    } catch (err) {
      const errorData = err.response?.data;
      let errorMessage = 'Failed to record bulk payment';
      if (errorData?.error) errorMessage = errorData.error;
      else if (err.message) errorMessage = err.message;
      setError(errorMessage);
      console.error('Bulk payment error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const sumAlloc = sumAllocations();
  const totalAmount = parseFloat(formData.total_amount) || 0;
  const isValidSum = Math.abs(sumAlloc - totalAmount) <= 0.01;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-xl flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Bulk Payment</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 text-2xl transition-colors leading-none"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden">
          <div className="p-6 overflow-y-auto flex-1 space-y-4">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            {bankAccounts.length === 0 && !loadingAccounts && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md p-3">
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  No bank accounts found. Please create a bank account first.
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Bank Account <span className="text-red-500">*</span>
                </label>
                {loadingAccounts ? (
                  <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-sm">
                    Loading...
                  </div>
                ) : (
                  <Select
                    name="bank_account_id"
                    value={formData.bank_account_id}
                    onChange={handleFormChange}
                    required
                  >
                    <option value="">Select bank account</option>
                    {bankAccounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.account_name} {account.account_type && `(${account.account_type})`}
                      </option>
                    ))}
                  </Select>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Total Amount <span className="text-red-500">*</span>
                </label>
                <Input
                  type="number"
                  name="total_amount"
                  value={formData.total_amount}
                  onChange={handleFormChange}
                  required
                  min="0.01"
                  step="0.01"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Payment Date
                </label>
                <Input
                  type="date"
                  name="payment_date"
                  value={formData.payment_date}
                  onChange={handleFormChange}
                />
              </div>
            </div>

            {/* Helper buttons */}
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleDistributeEqually}
                disabled={!formData.total_amount || parseFloat(formData.total_amount) <= 0}
              >
                Distribute equally
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleDistributeByBalance}
                disabled={!formData.total_amount || parseFloat(formData.total_amount) <= 0}
              >
                Distribute by balance
              </Button>
            </div>

            {/* Allocations table */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Allocations
                </label>
                <span
                  className={`text-sm ${isValidSum ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}
                >
                  Sum: {formatCurrency(sumAlloc)}
                  {totalAmount > 0 && (
                    <span>
                      {' '}
                      ({isValidSum ? '✓ matches' : `≠ ${formatCurrency(totalAmount)}`})
                    </span>
                  )}
                </span>
              </div>
              <div className="border border-gray-300 dark:border-gray-600 rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-700/50">
                      <th className="text-left px-3 py-2 text-gray-700 dark:text-gray-300 font-medium">
                        Invoice
                      </th>
                      <th className="text-left px-3 py-2 text-gray-700 dark:text-gray-300 font-medium">
                        Client
                      </th>
                      <th className="text-right px-3 py-2 text-gray-700 dark:text-gray-300 font-medium">
                        Balance Due
                      </th>
                      <th className="text-right px-3 py-2 text-gray-700 dark:text-gray-300 font-medium w-40">
                        Amount to allocate
                      </th>
                      <th className="w-24"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {allocations.map((alloc, index) => (
                      <tr
                        key={alloc.invoice_id}
                        className="border-t border-gray-200 dark:border-gray-600 hover:bg-gray-50/50 dark:hover:bg-gray-700/30"
                      >
                        <td className="px-3 py-2 text-gray-900 dark:text-gray-100 font-medium">
                          {alloc.invoice_num}
                        </td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400 truncate max-w-[150px]">
                          {alloc.client_name}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-900 dark:text-gray-100 tabular-nums">
                          {formatCurrency(alloc.balance_due)}
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            type="number"
                            value={alloc.amount}
                            onChange={(e) => handleAllocationChange(index, e.target.value)}
                            min="0"
                            max={alloc.balance_due}
                            step="0.01"
                            variant="table-number"
                            className="text-right"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePayFull(index)}
                            className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700"
                          >
                            Pay full
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Reference (Optional)
                </label>
                <Input
                  type="text"
                  name="reference"
                  value={formData.reference}
                  onChange={handleFormChange}
                  placeholder="Payment reference"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Notes (Optional)
                </label>
                <Input
                  type="text"
                  name="notes"
                  value={formData.notes}
                  onChange={handleFormChange}
                  placeholder="Additional notes"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700 shrink-0">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={loading || bankAccounts.length === 0}
            >
              {loading ? 'Recording Payments...' : 'Record Bulk Payment'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BulkPaymentModal;
